import React, { useEffect, useRef, useState } from 'react';
import type { TextFieldProps } from '@mui/material';
import {
  DEFAULT_DEBOUNCE_TIMEOUT,
  WithPassedPatchableGroupProps
} from '@/components/patcher/PatchableGroup';
import { passthrough } from '@/util';

type UseInputPatcherControlsProps = {
  name: string;
  transformValue?: (val?: string) => string | undefined;
  externalOnBlur?: TextFieldProps['onBlur'];
  convertBeforePatch?: (val?: string) => any;
  ref?: React.RefObject<HTMLInputElement | null>;
};

export const useInputPatcherControls = <T>({
  name,
  externalOnBlur,
  patchable,
  transformValue = passthrough,
  ref
}: WithPassedPatchableGroupProps<T, UseInputPatcherControlsProps>) => {
  const [instantVal, setInstantVal] = useState(
    transformValue(patchable?.currentValue) || ''
  );
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  if (!patchable) {
    throw new Error(
      'A Patcher component may be used as a child of a PatchableGroup'
    );
  }

  useEffect(() => {
    setInstantVal(transformValue(patchable?.currentValue) || '');
  }, [patchable?.currentValue, transformValue]);

  const doPatch = (v: string) => {
    if (patchable && patchable.currentValue !== v) {
      if (!patchable.currentValue && !v) {
        return Promise.resolve();
      }
      const { onPatch } = patchable;
      return onPatch({ [name]: v });
    }
    return Promise.resolve();
  };

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setInstantVal(e.target.value);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      doPatch(e.target.value).then(() => {
        setTimeout(() => ref?.current?.focus({ preventScroll: true }));
      });
    }, DEFAULT_DEBOUNCE_TIMEOUT);
  };

  const onBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    externalOnBlur?.(e);
    if (patchable.readOnly) {
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (!instantVal && !patchable.currentValue) {
      return;
    }
    if (instantVal !== (transformValue(patchable.currentValue) || '')) {
      doPatch(instantVal);
    }
  };

  return {
    onBlur,
    onChange,
    instantValue: instantVal
  };
};
